import * as React from 'react';

/**
 * Per-user colour theme (Light / Dark / System).
 *
 * The user's CHOICE is stored in localStorage; the RESOLVED theme (light | dark)
 * is written to `<html data-theme>`, which is what `global.css` keys its dark
 * tokens on. In System mode the resolved theme follows `prefers-color-scheme`
 * live. The inline script in `apps/web/index.html` does the same resolve before
 * first paint so the page never flashes the wrong theme - keep its storage key
 * and logic in sync with this file.
 *
 * Per browser, not per account: nothing is saved server-side.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Must match the key in the pre-paint script in apps/web/index.html. */
export const THEME_STORAGE_KEY = 'protoss.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

export function parseThemePreference(value: string | null | undefined): ThemePreference {
  return value === 'light' || value === 'dark' ? value : 'system';
}

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean
): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

function readStoredPreference(): ThemePreference {
  try {
    return parseThemePreference(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    // Storage blocked (private mode, disabled site data): fall back to System.
    return 'system';
  }
}

function writeStoredPreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Storage blocked: the choice still applies for this page load.
  }
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

// ── Store ───────────────────────────────────────────────────────────────────

interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
}

let state: ThemeState | null = null;
const listeners = new Set<() => void>();
let mediaSubscribed = false;

function applyResolved(resolved: ResolvedTheme): void {
  document.documentElement.dataset.theme = resolved;
}

function computeState(preference: ThemePreference): ThemeState {
  return { preference, resolved: resolveTheme(preference, systemPrefersDark()) };
}

function setState(next: ThemeState): void {
  const prev = state;
  state = next;
  applyResolved(next.resolved);
  if (!prev || prev.preference !== next.preference || prev.resolved !== next.resolved) {
    listeners.forEach((listener) => listener());
  }
}

function ensureInitialized(): ThemeState {
  if (!state) setState(computeState(readStoredPreference()));
  if (!mediaSubscribed && typeof window.matchMedia === 'function') {
    mediaSubscribed = true;
    window.matchMedia(DARK_QUERY).addEventListener('change', () => {
      if (state?.preference === 'system') setState(computeState('system'));
    });
    // Another tab changed the choice: follow it.
    window.addEventListener('storage', (event) => {
      if (event.key === THEME_STORAGE_KEY || event.key === null) {
        setState(computeState(readStoredPreference()));
      }
    });
  }
  return state as ThemeState;
}

/**
 * Apply the stored theme and start following the OS while in System mode.
 * Called once from main.tsx so System mode stays live on every page, including
 * pages that never render the theme switch (sign-in).
 */
export function initTheme(): void {
  ensureInitialized();
}

export function setThemePreference(preference: ThemePreference): void {
  ensureInitialized();
  writeStoredPreference(preference);
  setState(computeState(preference));
}

function subscribe(listener: () => void): () => void {
  ensureInitialized();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ThemeState {
  return ensureInitialized();
}

/** The user's choice (Light / Dark / System) and a setter. */
export function useThemePreference(): [ThemePreference, (preference: ThemePreference) => void] {
  const current = React.useSyncExternalStore(subscribe, getSnapshot);
  return [current.preference, setThemePreference];
}

/** The theme actually showing (System already resolved). Use for JS-themed widgets (Monaco). */
export function useResolvedTheme(): ResolvedTheme {
  return React.useSyncExternalStore(subscribe, getSnapshot).resolved;
}
